import { createContext, useContext } from 'react';

/** Status info for a single unit */
export interface StatusInfo {
  status: 'online' | 'offline' | 'idle' | 'unknown';
  soc?: number;
  lastUpdate: string;
  warning_count?: number;
  fault_count?: number;
}

/** Context type for unit status polling */
export interface UnitStatusContextType {
  /** Map of unitId to status info - O(1) lookups */
  statuses: Map<string, StatusInfo>;

  /** Whether polling is currently active */
  isPolling: boolean;

  /** Last successful poll timestamp */
  lastPollTime: Date | null;

  /** Error from most recent poll attempt, null if successful */
  pollError: string | null;

  /** Get status for a specific unit (returns 'unknown' if not found) */
  getStatus: (unitId: string) => 'online' | 'offline' | 'idle' | 'unknown';

  /** Get full status info for a unit */
  getStatusInfo: (unitId: string) => StatusInfo | null;

  /** Manually trigger an immediate status refresh */
  refreshNow: () => Promise<void>;

  /** Total count of online units */
  onlineCount: number;

  /** Total count of units with status data */
  totalCount: number;
}

export const UnitStatusContext = createContext<UnitStatusContextType | undefined>(undefined);

/**
 * Hook to access unit status data
 *
 * @throws Error if used outside of UnitStatusProvider
 */
export function useUnitStatus(): UnitStatusContextType {
  const context = useContext(UnitStatusContext);
  if (context === undefined) {
    throw new Error('useUnitStatus must be used within a UnitStatusProvider');
  }
  return context;
}

/**
 * Optional hook that returns undefined if outside provider
 * Useful for components that may or may not be in provider tree
 */
export function useUnitStatusOptional(): UnitStatusContextType | undefined {
  return useContext(UnitStatusContext);
}
